import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import { getIcon, tagLabel } from "../lib/concepts";
import { useLang } from "../lib/i18n";

export const ConceptOverlay = ({ concept, onClose, onUse, shortlisted, onToggleShortlist }) => {
  const [tab, setTab] = useState(0);
  const { t, tf, sectorName, tagL } = useLang();
  const TABS = t("overlay.tabs");

  useEffect(() => {
    setTab(0);
    const fn = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [concept, onClose]);

  if (!concept) return null;
  const Icon = getIcon(concept.icon);

  return (
    <AnimatePresence>
      <motion.div data-testid="concept-overlay" role="dialog" aria-modal="true" aria-label={concept.title}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
        onClick={onClose}>
        <motion.div
          initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="bg-cream w-full max-w-3xl h-[92vh] sm:h-auto sm:max-h-[85vh] flex flex-col border-t-2 border-gold sm:border sm:border-gold/60">
          <div className="flex items-start justify-between p-6 md:p-10 pb-0">
            <div>
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <span className="text-[10px] tracking-[0.3em] uppercase text-green/60">{concept.ref}</span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-green/60">· {sectorName(concept.sector)}</span>
                {concept.confirmed && <span className="text-[9px] tracking-[0.25em] uppercase bg-gold text-ink px-2 py-0.5">{t("library.confirmed")}</span>}
              </div>
              <h3 data-testid="overlay-title" className="font-display text-3xl md:text-4xl text-ink flex items-center gap-4">
                <Icon size={30} strokeWidth={1.25} className="text-gold shrink-0" />
                {concept.title}
              </h3>
            </div>
            <button data-testid="overlay-close" onClick={onClose} aria-label="Close dossier"
              className="p-2 text-ink/50 hover:text-ink transition-colors duration-300">
              <X size={22} />
            </button>
          </div>

          <div data-testid="overlay-tabs" className="flex gap-1 px-6 md:px-10 mt-8 border-b border-ink/15 overflow-x-auto">
            {TABS.map((label, i) => (
              <button key={label} data-testid={`overlay-tab-${["overview","records","reports","settings"][i]}`} onClick={() => setTab(i)}
                className={`px-4 py-3 text-[11px] tracking-[0.25em] uppercase whitespace-nowrap border-b-2 -mb-px transition-colors duration-300 ${
                  tab === i ? "border-gold text-ink" : "border-transparent text-ink/40 hover:text-ink"
                }`}>
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-10">
            {tab === 0 && (
              <div data-testid="overlay-overview" className="space-y-8">
                <p className="font-display text-xl md:text-2xl text-ink leading-relaxed">{concept.promise}</p>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-green/60 mb-3">{t("overlay.removes")}</div>
                  <div className="flex flex-wrap gap-2">
                    {(concept.tags || []).map((tg) => (
                      <span key={tg} className="text-xs border border-ink/20 px-3 py-1.5 text-ink/70">{tagL(tg, tagLabel(tg))}</span>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-px bg-ink/15 border border-ink/15">
                  {t("overlay.stats").map(([v, l]) => (
                    <div key={l} className="bg-cream p-4">
                      <div className="font-display text-lg text-ink">{v}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-ink/50 mt-1">{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tab === 1 && (
              <div data-testid="overlay-records" className="space-y-px bg-ink/10 border border-ink/10">
                {(concept.records || []).map((r, i) => (
                  <div key={i} className="bg-cream p-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                    <span className="text-[11px] tracking-[0.2em] uppercase text-gold font-medium shrink-0 w-28">{r.date}</span>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-green/60 shrink-0 w-24">{r.type}</span>
                    <span className="text-sm text-ink/75 leading-relaxed">{r.summary}</span>
                  </div>
                ))}
                {(!concept.records || concept.records.length === 0) && (
                  <p className="bg-cream p-5 text-sm text-ink/50 italic">{t("overlay.noRecords")}</p>
                )}
              </div>
            )}
            {tab === 2 && (
              <div data-testid="overlay-reports" className="space-y-6">
                <p className="text-[10px] tracking-[0.3em] uppercase text-ink/40">{t("overlay.demoReports")}</p>
                <div className="grid sm:grid-cols-2 gap-px bg-ink/15 border border-ink/15">
                  {t("overlay.reportMetrics").map(([l, v]) => (
                    <div key={l} className="bg-cream p-6">
                      <div className="font-display text-3xl text-ink">{v}</div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-ink/50 mt-2">{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tab === 3 && (
              <div data-testid="overlay-settings" className="space-y-px bg-ink/10 border border-ink/10">
                <p className="bg-cream p-4 text-[10px] tracking-[0.3em] uppercase text-ink/40">{t("overlay.demoSettings")}</p>
                {t("overlay.settingsRows").map(([l, v]) => (
                  <div key={l} className="bg-cream p-5 flex items-center justify-between gap-4">
                    <span className="text-sm text-ink/70">{l}</span>
                    <span className="text-sm font-display text-ink">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 md:px-10 border-t border-ink/15 flex flex-wrap items-center gap-5">
            <button data-testid="overlay-use-btn" onClick={() => onUse(concept)}
              className="group flex items-center gap-3 bg-ink text-cream text-xs tracking-[0.2em] uppercase px-7 py-4 hover:bg-green transition-colors duration-300">
              {t("overlay.use")} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
            </button>
            <button data-testid="overlay-shortlist-btn"
              onClick={() => {
                onToggleShortlist(concept.ref);
                toast.success(
                  shortlisted ? tf("overlay.toastRm", { ref: concept.ref }) : tf("overlay.toastAdd", { ref: concept.ref }),
                  { description: shortlisted ? undefined : t("overlay.toastSub") }
                );
              }}
              className={`flex items-center gap-2 text-xs tracking-[0.2em] uppercase px-6 py-4 border transition-colors duration-300 ${
                shortlisted ? "border-gold bg-gold/15 text-ink" : "border-ink/30 text-ink/60 hover:border-gold hover:text-ink"
              }`}>
              {shortlisted ? <BookmarkCheck size={14} className="text-gold" /> : <Bookmark size={14} />}
              {shortlisted ? t("overlay.added") : t("overlay.add")}
            </button>
            <span className="text-xs text-ink/45">{tf("overlay.quote", { ref: concept.ref })}</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
